import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { BackendEvent } from '../data/api/admin-events.api-datasource'
import type { EventFormData } from './event-form.schema'

dayjs.extend(utc)
dayjs.extend(timezone)

/** Convert UTC ISO string back to datetime-local format in the event's timezone. */
const utcToLocal = (utcStr: string | undefined, tz: string): string => {
  if (!utcStr) return ''
  return dayjs.utc(utcStr).tz(tz).format('YYYY-MM-DDTHH:mm')
}

/**
 * Convert backend datetime to datetime-local. Backend sometimes returns ISO
 * strings (`rsvp_start`) and sometimes `toDateString()` output ("Mon May 04 2026")
 * for meet & greet — `dayjs.utc` parses both via the underlying Date constructor.
 */
const backendDateToLocal = (value: string | undefined, tz: string): string => {
  if (!value) return ''
  const d = dayjs.utc(new Date(value))
  if (!d.isValid()) return ''
  return d.tz(tz).format('YYYY-MM-DDTHH:mm')
}

export const mapEventToFormData = (event: BackendEvent): Partial<EventFormData> => {
  const tz = event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  const earlyAccess = event.features?.earlyAccess
  const vip = event.features?.vipTicketSales
  const meet = event.fanMeetAndGreet

  return {
    eventName: event.title,
    timezone: event.timezone ?? '',
    eventDate: event.startsAt?.split('T')[0] ?? '',
    displayUntil: utcToLocal(event.endsAt, tz),
    publishAt: utcToLocal(event.publishAt ?? undefined, tz),
    isPinned: event.isPinned ?? false,
    imageUrl: event.imageUrl ?? '',
    venueName: event.venue?.name ?? '',
    addressLine1: event.venue?.address?.addressLine1 ?? '',
    addressLine2: event.venue?.address?.addressLine2 ?? '',
    city: event.venue?.address?.city ?? '',
    state: event.venue?.address?.state ?? '',
    zipCode: event.venue?.address?.zipCode ?? '',
    country: event.venue?.address?.country ?? '',
    ticketButtonText: event.features?.ticketSales?.buttonTitle ?? '',
    ticketUrl: event.features?.ticketSales?.url ?? event.features?.presale?.url ?? '',
    presaleCode: event.features?.presale?.code ?? '',
    presaleStart: utcToLocal(event.presaleStart, tz),
    presaleEnd: utcToLocal(event.presaleEnd, tz),

    // Early Access (RSVP)
    rsvpButtonText: earlyAccess?.buttonTitle ?? '',
    rsvpCapacity: earlyAccess?.maxRsvp,
    rsvpStart: utcToLocal(earlyAccess?.rsvpStart, tz),
    rsvpEnd: utcToLocal(earlyAccess?.rsvpEnd, tz),
    rsvpInfo: earlyAccess?.rsvpInfo ?? '',
    rsvpLeaveInfo: earlyAccess?.rsvpLeaveInfo ?? '',

    // VIP ticket sales
    vipUrl: vip?.url ?? '',
    vipButtonText: vip?.buttonTitle ?? '',

    // Meet & Greet (addons)
    meetButtonText: meet?.fanMeetAndGreetButtonText ?? '',
    meetAndGreetStart: backendDateToLocal(meet?.fanMeetAndGreetStart, tz),
    meetAndGreetEnd: backendDateToLocal(meet?.fanMeetAndGreetEnd, tz),
    meetAndGreetJoinText: meet?.fanMeetAndGreetDialogJoinText ?? '',
    meetAndGreetLeaveText: meet?.fanMeetAndGreetDialogLeaveText ?? ''
  }
}
